/**
 * Kardume Urbano design tokens - official brand palette.
 *
 * Source of truth: `assets/paleta de cores.png` (repo root). Identity is oceanic
 * (fish school + city) with a warm-white base, deep "night blue" and an orange
 * "experience" accent. Default system radius: 8px.
 *
 * Official palette:
 *   Azul Urbano       #07507A   (primary brand blue)
 *   Azul-Petróleo     #07989A   (teal / petrol)
 *   Laranja Experiência #FF8A2B (accent / CTA highlight)
 *   Azul Noturno      #102735   (dark navy - text, sidebar, dark bg)
 *   Branco Quente     #F8F7F2   (warm white background)
 *   Cinza Interface   #E4E8E8   (interface gray / borders)
 *
 * `theme` = light theme (default). `darkTheme` = dark theme (Dark Mode is a
 * product requirement). Both share the exact same keys so styled components
 * type against a single `DefaultTheme`.
 */

export type AppTheme = {
  // Brand
  primary: string;
  primaryDark: string;
  primaryLight: string;
  petrol: string;
  petrolDark: string;
  accent: string;
  accentDark: string;
  navy: string;
  navySoft: string;

  // Neutrals
  black: string;
  blackSoft: string;
  darkGray: string;
  mediumGray: string;
  lightGray: string;

  // Surfaces
  background: string;
  surface: string;
  surfaceAlt: string;
  border: string;

  // Semantic
  success: string;
  warning: string;
  error: string;
  info: string;

  // Text
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  textInverted: string;

  // Shape
  radius: string;
  radiusLg: string;
  radiusPill: string;

  // Shadow
  shadowSm: string;
  shadowMd: string;
};

/** Raw official brand colors (do not alias these away - they are the guide). */
export const brandColors = {
  azulUrbano: '#07507A',
  azulPetroleo: '#07989A',
  laranjaExperiencia: '#FF8A2B',
  azulNoturno: '#102735',
  brancoQuente: '#F8F7F2',
  cinzaInterface: '#E4E8E8',
} as const;

const brand = {
  primary: brandColors.azulUrbano,
  primaryDark: '#063E60',
  primaryLight: '#E2EDF3',
  petrol: brandColors.azulPetroleo,
  petrolDark: '#06777A',
  accent: brandColors.laranjaExperiencia,
  accentDark: '#E5751A',
  navy: brandColors.azulNoturno,
  navySoft: '#1C3A4C',
};

const shape = {
  radius: '8px',
  radiusLg: '16px',
  radiusPill: '999px',
};

const semantic = {
  success: '#1E9E6A',
  warning: '#D97706',
  error: '#DC2626',
  info: brandColors.azulPetroleo,
};

export const theme: AppTheme = {
  ...brand,
  ...shape,
  ...semantic,

  black: brandColors.azulNoturno,
  blackSoft: '#1C3A4C',
  darkGray: '#33454F',
  mediumGray: '#5E6E77',
  lightGray: brandColors.cinzaInterface,

  background: brandColors.brancoQuente,
  surface: '#FFFFFF',
  surfaceAlt: '#F1F3F0',
  border: brandColors.cinzaInterface,

  textPrimary: brandColors.azulNoturno,
  textSecondary: '#3A4C57',
  textMuted: '#7A8A92',
  textInverted: '#FFFFFF',

  shadowSm: '0 1px 2px rgba(16, 39, 53, 0.06)',
  shadowMd: '0 4px 16px rgba(16, 39, 53, 0.10)',
};

export const darkTheme: AppTheme = {
  ...brand,
  ...shape,
  ...semantic,

  primaryLight: '#0C3A55',

  black: '#F8F7F2',
  blackSoft: '#E6EBEE',
  darkGray: '#C3CCD3',
  mediumGray: '#8B99A2',
  lightGray: '#1C3A4C',

  background: brandColors.azulNoturno,
  surface: '#16303F',
  surfaceAlt: '#1C3A4C',
  border: '#274454',

  textPrimary: '#EEF3F6',
  textSecondary: '#B7C4CC',
  textMuted: '#7E8F98',
  textInverted: brandColors.azulNoturno,

  shadowSm: '0 1px 2px rgba(0, 0, 0, 0.3)',
  shadowMd: '0 6px 20px rgba(0, 0, 0, 0.45)',
};

export default theme;
